import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Alert, 
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { extractAllPdfTexts } from '../services/cloudFunctionsService';

/**
 * Admin utility component for migrating existing PDFs
 * This triggers text extraction for PDFs that were uploaded before the feature was implemented
 */
const PdfMigrationUtility = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleMigrateAll = async () => {
    if (!window.confirm('This will process all PDFs in Benny\'s Journal. Continue?')) {
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const migrationResult = await extractAllPdfTexts();
      setResult(migrationResult);
    } catch (err) {
      console.error('Migration failed:', err);
      setError(err.message || 'Migration failed. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 4, backgroundColor: '#2c2c2c', border: '2px solid #d98d2a', borderRadius: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ color: '#d98d2a', fontWeight: 'bold', mb: 2 }}>
        📄 PDF Text Extraction Migration
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 3, color: '#f5f5f5' }}>
        Extract searchable text from existing PDFs in Benny's Journal. 
        This enables the powerful PDF content search feature.
      </Typography>

      <Alert severity="info" sx={{ mb: 2, backgroundColor: '#1a237e', color: '#e3f2fd' }}>
        <Typography variant="body2">
          <strong>What this does:</strong> Processes all PDFs uploaded before the search feature was implemented.
          The extraction happens in the cloud and takes ~5 seconds per PDF.
          PDFs with existing text will be skipped automatically.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Alert severity="success" sx={{ mb: 2, backgroundColor: '#1b5e20', color: '#e8f5e9' }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            ✅ Migration Complete!
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary={`Total Processed: ${result.processed || 0}`}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary={`Successful: ${result.successful || 0}`}
                primaryTypographyProps={{ style: { color: '#4CAF50' } }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary={`Failed: ${result.failed || 0}`}
                primaryTypographyProps={{ style: { color: result.failed > 0 ? '#f44336' : 'inherit' } }}
              />
            </ListItem>
          </List>
          {result.errors && result.errors.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" display="block" fontWeight="bold">
                Errors:
              </Typography>
              {result.errors.map((err, idx) => (
                <Typography key={idx} variant="caption" display="block" sx={{ color: '#f44336' }}>
                  {err.entryId}: {err.error}
                </Typography>
              ))}
            </Box>
          )}
        </Alert>
      )}

      <Button
        variant="contained"
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
        onClick={handleMigrateAll}
        disabled={loading}
        fullWidth
        sx={{
          backgroundColor: '#d98d2a',
          color: 'white',
          fontWeight: 'bold',
          py: 1.5,
          '&:hover': { backgroundColor: '#b8741f' },
          '&:disabled': { backgroundColor: '#666', color: '#aaa' }
        }}
      >
        {loading ? 'Processing PDFs...' : 'Extract Text from All PDFs'}
      </Button>

      <Typography variant="caption" display="block" sx={{ mt: 2, color: '#aaa', textAlign: 'center' }}>
        💡 Tip: After migration, users can search inside PDFs from the navbar search bar!
      </Typography>
    </Paper>
  );
};

export default PdfMigrationUtility;
